import React from "react";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import { FadeInArray } from "source/app/presentation/Shared/Animation/FadeInArray";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Glyphicon from "source/app/presentation/Shared/Glyphicon/Glyphicon";
import Hyperlink from "source/app/presentation/Shared/Link/Hyperlink";
import { StyleClassAuto } from "source/app/presentation/Shared/Styles/Auto/StyleClassAuto";
import Ambience from "source/app/services/Sound/Ambience/Ambience";
import { ESharedComponent } from "source/app/services/UI/State/ESharedComponent";
import { State } from "source/app/services/UI/State/State";
import styled from "styled-components";
import { IAmbienceMenuState } from "./IAmbienceMenuState";
import { AdjustAmbienceVolume } from "./Items/AdjustAmbienceVolume";
import { CurrentAmbienceInfo } from "./Items/CurrentAmbienceInfo";
import { NextAmbienceIcon } from "./Items/NextAmbienceIcon";
import { ToggleAmbience } from "./Items/ToggleAmbience";
import { LoadingProgress } from "../Shared/LoadingProgress";

export default class AmbienceMenu extends React.Component<
    {},
    IAmbienceMenuState
> {
    dropdownRef: React.RefObject<HTMLDivElement>;

    constructor(props: {}) {
        super(props);

        this.state = {
            showMenu: false,
            currentAmbienceSoundFile: undefined,
            currentFileInfo: undefined,
            cachedB2FileInfos: undefined,
            allFileInfos: [],
            currentVolume: 1.0,
            selectedAmbience: undefined,
            loadingProgressPercent: 0,
        };

        this.dropdownRef = React.createRef();
    }

    async componentDidMount() {
        State.ShareClass(ESharedComponent.AmbienceMenu, this);
        this.handleMenuToggling();
    }

    async componentDidUpdate(
        prevProps: Readonly<{}>,
        prevState: Readonly<IAmbienceMenuState>,
        snapshot?: any
    ) {
        if (prevState.currentFileInfo !== this.state.currentFileInfo) {
            if (this.state.currentFileInfo) {
                const currentAmbienceSoundFile = Ambience.GetAmbienceSoundFile(
                    this.state.currentFileInfo
                );

                this.setState({
                    currentAmbienceSoundFile,
                    loadingProgressPercent: 0,
                });

                currentAmbienceSoundFile.Play({
                    loop: true,
                    onPlay: () =>
                        this.setState({ loadingProgressPercent: undefined }),
                    onDownloadProgressUpdate: (progressPercent) => {
                        // console.log(
                        //     `[Ambience]: ${progressPercent}% downloaded`
                        // );

                        this.setState({
                            loadingProgressPercent: progressPercent,
                        });
                    },
                });

                console.log(
                    `[Ambience] Started '${this.state.currentFileInfo.fileNameWithoutExtension}'`
                );
            } else {
                this.setState({ currentAmbienceSoundFile: undefined });

                console.log(
                    `[Ambience] Stopped '${
                        prevState.currentFileInfo!.fileNameWithoutExtension
                    }'`
                );
            }
        }

        State.ShareClass(ESharedComponent.AmbienceMenu, this);
    }

    handleMenuToggling() {
        const handleClickOutside = (event: any) => {
            if (
                this.dropdownRef !== null &&
                this.dropdownRef.current !== null &&
                !this.dropdownRef.current.contains(event.target)
            ) {
                this.setState({ ...this.state, showMenu: false });
            }
        };

        // Attach the listener to the document
        document.addEventListener("mousedown", handleClickOutside);

        // Cleanup the listener when the component unmounts
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }

    render() {
        if (IsMobileDevice()) {
            return null;
        }

        const isAmbienceOn =
            this.state.currentAmbienceSoundFile?.IsPlaying() ?? false;

        const subMenuItems = [
            <SubMenuItemWithBorder>
                <ToggleAmbience isAmbienceOn={isAmbienceOn} />
            </SubMenuItemWithBorder>,
            <SubMenuItem>
                <CurrentAmbienceInfo ambienceMenu={this} />
            </SubMenuItem>,
            <SubMenuItemWithBorder>
                <NextAmbienceIcon />
            </SubMenuItemWithBorder>,
            <SubMenuItemWithBorder>
                <AdjustAmbienceVolume volume={this.state.currentVolume} />
            </SubMenuItemWithBorder>,
        ];

        const isLoading = this.state.loadingProgressPercent !== undefined;

        return (
            <Container ref={this.dropdownRef}>
                <Hyperlink
                    whenClicked={() =>
                        this.setState({
                            ...this.state,
                            showMenu: !this.state.showMenu,
                        })
                    }
                    classes={StyleClassAuto({
                        color: isLoading ? `orange` : `rgb(208, 208, 208)`,
                        opacity: isAmbienceOn || isLoading ? 1.0 : 0.5,
                        transition: `all 0.5s ease !important`,
                    })}
                >
                    <div
                        className={StyleClassAuto({
                            position: `relative`,
                            display: `inline-block`,
                        })}
                    >
                        <Glyphicon icon={EGlyphicon.cloud} />
                        <LoadingProgress
                            loadingProgressPercent={
                                this.state.loadingProgressPercent
                            }
                        />
                    </div>
                </Hyperlink>
                <div
                    className={GetDropDownContentStyleClass(
                        this.state.showMenu
                    )}
                >
                    <FadeInArray
                        label=""
                        render={(item) => item}
                        array={subMenuItems}
                        itemDelayMs={50}
                    />
                </div>
            </Container>
        );
    }
}

const Container = styled.div`
    position: relative;
    display: inline-block;
    margin: 0px 5px;
`;

const GetDropDownContentStyleClass = (show: boolean) =>
    StyleClassAuto({
        display: show ? "flex" : "none",
        flexDirection: "column",
        position: "absolute",
        minWidth: "200px",
        boxShadow: "0px 8px 16px 0px rgba(0, 0, 0, 0.2)",
        padding: "12px 16px",
        zIndex: 1,
        right: 0,
        background: "#2e2e2e57",
        borderRadius: "4px",
    });

const SubMenuItem = styled.div`
    padding: 5px;
`;

const SubMenuItemWithBorder = styled(SubMenuItem)`
    border-radius: 3px;
    border: 1px dotted #c0c0c017;
    &:hover {
        border: 1px solid #c0c0c070;
    }
`;
